import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  connectAuthPost, connectUploadImage, getConnectToken,
  setConnectUser, clearConnectSession, normalizeUser, normalizePost
} from './connectApi';
import PostComments from './PostComments';
import ComposerImage from './ComposerImage';
import NotificationsBell from './NotificationsBell';
import ConnectSetup from './ConnectSetup';
import ConnectNav from './ConnectNav';
import TravelPlans from './TravelPlans';
import FollowsList from './FollowsList';
import './styles/ConnectProfile.css';
import './styles/ProfileLayout.css';

export default function ConnectProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  // 'followers' | 'following' while that list is open
  const [followsTab, setFollowsTab] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  // Object URLs for an avatar or cover still uploading
  const [uploadPreview, setUploadPreview] = useState({ avatar: '', cover: '' });
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Profile data (extended from registration)
  const [profileData, setProfileData] = useState({
    // From registration
    fullName: '',
    gender: '',
    birthDate: '',
    email: '',
    countryOfResidence: '',
    currentLocation: '',
    // Onboarding fields
    pronouns: '',
    genderIdentity: '',
    sexualOrientation: '',
    visibility: 'public',
    homeDestinationId: null,
    bio: '',
    travelStyle: '',
    favoriteDestinations: [],
    // Profile extras
    profilePicture: '',
    coverPhoto: ''
  });

  const [destinationInput, setDestinationInput] = useState('');
  const [activeTab, setActiveTab] = useState('posts');
  // Open destinations, so the sidebar can name the member's home base
  const [destinations, setDestinations] = useState([]);
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);

  // Posts (loaded from API)
  const [posts, setPosts] = useState([]);

  // Post composer
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostLocation, setNewPostLocation] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const postImageInputRef = useRef(null);
  const [newPostImageFile, setNewPostImageFile] = useState(null);

  // Avatar / cover upload
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  // Options
  const pronounOptions = [
    'he/him', 'she/her', 'they/them', 'he/they', 'she/they',
    'ze/zir', 'xe/xem', 'Any pronouns', 'Prefer not to say', 'Custom'
  ];

  const genderIdentityOptions = [
    'Cisgender Man', 'Cisgender Woman', 'Transgender Man', 'Transgender Woman',
    'Non-binary', 'Genderqueer', 'Genderfluid', 'Agender', 'Bigender',
    'Two-Spirit', 'Questioning', 'Prefer not to say', 'Other'
  ];

  const orientationOptions = [
    'Gay', 'Lesbian', 'Bisexual', 'Pansexual', 'Queer', 'Asexual',
    'Demisexual', 'Straight Ally', 'Questioning', 'Prefer not to say', 'Other'
  ];

  const visibilityOptions = [
    {
      value: 'public',
      label: 'Public',
      desc: 'Anyone can see your profile, and your posts appear in the feed'
    },
    {
      value: 'community',
      label: 'Community Only',
      desc: 'Members can see your profile; your posts reach only the people who follow you'
    },
    {
      value: 'followers',
      label: 'Followers Only',
      desc: 'People can find you and ask to follow, but only followers see your profile and posts'
    },
    {
      value: 'private',
      label: 'Private',
      desc: 'You are hidden from Connect, and your posts stay off the feed'
    }
  ];

  const travelStyleOptions = [
    'Solo Adventurer', 'Couple Travel', 'Group Explorer', 'Luxury Seeker',
    'Budget Backpacker', 'Cultural Immersion', 'Beach & Relaxation',
    'Nightlife & Parties', 'Nature & Outdoors', 'Foodie Journey'
  ];

  const suggestedDestinations = [
    'Puerto Rico', 'Curaçao', 'Aruba', 'Jamaica', 'Barbados',
    'Dominican Republic', 'St. Lucia', 'Bahamas', 'Trinidad', 'Martinique'
  ];

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const applyUser = (userData) => {
    setUser(userData);
    setConnectUser(userData);
    setProfileData(prev => ({
      ...prev,
      fullName: userData.fullName || '',
      gender: userData.gender || '',
      birthDate: userData.birthDate || '',
      email: userData.email || '',
      countryOfResidence: userData.countryOfResidence || '',
      currentLocation: userData.currentLocation || '',
      pronouns: userData.pronouns || '',
      genderIdentity: userData.genderIdentity || '',
      sexualOrientation: userData.sexualOrientation || '',
      visibility: userData.visibility || 'public',
      bio: userData.bio || '',
      travelStyle: userData.travelStyle || '',
      favoriteDestinations: userData.favoriteDestinations || [],
      profilePicture: userData.profilePicture || '',
      coverPhoto: userData.coverPhoto || '',
      homeDestinationId: userData.homeDestinationId ?? null
    }));
  };

  useEffect(() => {
    // Require a valid session token
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }

    const load = async () => {
      try {
        const result = await connectAuthPost('connect/profile/get');
        if (!result.success) {
          clearConnectSession();
          navigate('/connect');
          return;
        }

        const userData = normalizeUser(result.data.user);
        applyUser(userData);

        if (!userData.onboardingComplete) {
          setShowOnboarding(true);
        }

        // Load own posts
        const postsResult = await connectAuthPost('connect/posts/list');
        if (postsResult.success) {
          setPosts(postsResult.data.posts.map(normalizePost));
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const saveProfileToApi = async (extra = {}) => {
    const payload = {
      full_name: profileData.fullName,
      current_location: profileData.currentLocation,
      country_of_residence: profileData.countryOfResidence,
      pronouns: profileData.pronouns,
      gender_identity: profileData.genderIdentity,
      sexual_orientation: profileData.sexualOrientation,
      visibility: profileData.visibility,
      bio: profileData.bio,
      travel_style: profileData.travelStyle,
      favorite_destinations: profileData.favoriteDestinations,
      ...extra
    };
    const result = await connectAuthPost('connect/profile/update', payload);
    if (result.success) {
      applyUser(normalizeUser(result.data.user));
      return true;
    }
    return false;
  };

  const homeDestinationName = destinations.find(
    (d) => String(d.id) === String(profileData.homeDestinationId)
  )?.name || '';

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const addDestination = (dest) => {
    if (dest && !profileData.favoriteDestinations.includes(dest)) {
      setProfileData(prev => ({
        ...prev,
        favoriteDestinations: [...prev.favoriteDestinations, dest]
      }));
    }
    setDestinationInput('');
  };

  const removeDestination = (dest) => {
    setProfileData(prev => ({
      ...prev,
      favoriteDestinations: prev.favoriteDestinations.filter(d => d !== dest)
    }));
  };

  const handleOnboardingNext = async () => {
    if (onboardingStep < 3) {
      setOnboardingStep(onboardingStep + 1);
    } else {
      // Complete onboarding — persist everything
      await saveProfileToApi({ onboarding_complete: 1 });
      setShowOnboarding(false);
    }
  };

  const handleOnboardingSkip = async () => {
    await connectAuthPost('connect/profile/update', { onboarding_complete: 1 });
    const updatedUser = { ...user, onboardingComplete: true };
    setUser(updatedUser);
    setConnectUser(updatedUser);
    setShowOnboarding(false);
  };

  const handleSaveProfile = async () => {
    const ok = await saveProfileToApi();
    if (ok) setIsEditing(false);
  };

  useEffect(() => {
    let cancelled = false;
    connectAuthPost('connect/destinations/list')
      .then((res) => {
        if (!cancelled && res.success) setDestinations(res.data.destinations || []);
      })
      .catch(() => { /* the sidebar falls back to showing nothing */ });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    try {
      await connectAuthPost('connect/auth/logout');
    } catch (err) {
      // Best-effort — clear locally regardless
    }
    clearConnectSession();
    navigate('/connect');
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    // Show the picked image straight away; waiting for the round trip made it
    // look as though nothing had happened.
    const localUrl = URL.createObjectURL(file);
    setUploadPreview(prev => ({ ...prev, [type]: localUrl }));
    setIsUploading(true);
    try {
      const result = await connectUploadImage(file, type);
      if (result.success) {
        const field = type === 'avatar' ? 'profilePicture' : 'coverPhoto';
        setProfileData(prev => ({ ...prev, [field]: result.data.url }));
        const updatedUser = { ...user, [field]: result.data.url };
        setUser(updatedUser);
        setConnectUser(updatedUser);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
      URL.revokeObjectURL(localUrl);
      setUploadPreview(prev => ({ ...prev, [type]: '' }));
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || isPosting) return;
    setIsPosting(true);
    try {
      let imageUrl = '';
      if (newPostImageFile) {
        const uploadResult = await connectUploadImage(newPostImageFile, 'post');
        if (uploadResult.success) imageUrl = uploadResult.data.url;
      }

      const result = await connectAuthPost('connect/posts/create', {
        content: newPostContent.trim(),
        image: imageUrl,
        location: newPostLocation.trim()
      });

      if (result.success) {
        setPosts(prev => [normalizePost(result.data.post), ...prev]);
        setNewPostContent('');
        setNewPostLocation('');
        setNewPostImageFile(null);
        if (postImageInputRef.current) postImageInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Failed to create post:', err);
    } finally {
      setIsPosting(false);
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      const result = await connectAuthPost('connect/posts/like', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, likes: result.data.likes, likedByViewer: result.data.liked }
            : p
        ));
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const result = await connectAuthPost('connect/posts/delete', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.filter(p => p.id !== postId));
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  if (!user) {
    return null;
  }

  // Onboarding Modal
  const renderOnboarding = () => (
    <div className="connect-onboarding-overlay">
      <div className="connect-onboarding-modal">
        <div className="connect-onboarding-header">
          <h2>Complete Your Profile</h2>
          <p>Help us personalize your experience (optional but encouraged)</p>
          <div className="connect-onboarding-progress">
            <div className="connect-progress-bar">
              <div 
                className="connect-progress-fill" 
                style={{ width: `${(onboardingStep / 3) * 100}%` }}
              ></div>
            </div>
            <span className="connect-progress-text">Step {onboardingStep} of 3</span>
          </div>
        </div>

        <div className="connect-onboarding-content">
          {/* Step 1: Identity */}
          {onboardingStep === 1 && (
            <div className="connect-onboarding-step">
              <h3>Your Identity</h3>
              <p className="connect-step-desc">
                Share as much or as little as you're comfortable with
              </p>

              <div className="connect-onboard-field">
                <label>Pronouns</label>
                <select
                  value={profileData.pronouns}
                  onChange={(e) => handleProfileChange('pronouns', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select pronouns</option>
                  {pronounOptions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="connect-onboard-field">
                <label>Gender Identity</label>
                <select
                  value={profileData.genderIdentity}
                  onChange={(e) => handleProfileChange('genderIdentity', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select gender identity</option>
                  {genderIdentityOptions.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="connect-onboard-field">
                <label>Sexual Orientation</label>
                <select
                  value={profileData.sexualOrientation}
                  onChange={(e) => handleProfileChange('sexualOrientation', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select orientation</option>
                  {orientationOptions.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Visibility & Bio */}
          {onboardingStep === 2 && (
            <div className="connect-onboarding-step">
              <h3>About You</h3>
              <p className="connect-step-desc">
                Tell the community a bit about yourself
              </p>

              <div className="connect-onboard-field">
                <label>Profile Visibility</label>
                <div className="connect-visibility-options">
                  {visibilityOptions.map(opt => (
                    <div
                      key={opt.value}
                      className={`connect-visibility-option ${profileData.visibility === opt.value ? 'selected' : ''}`}
                      onClick={() => handleProfileChange('visibility', opt.value)}
                    >
                      <div className="connect-visibility-radio">
                        {profileData.visibility === opt.value && <div className="connect-radio-dot"></div>}
                      </div>
                      <div className="connect-visibility-info">
                        <span className="connect-visibility-label">{opt.label}</span>
                        <span className="connect-visibility-desc">{opt.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="connect-onboard-field">
                <label>Bio</label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  className="connect-onboard-textarea"
                  placeholder="Tell us about yourself, your interests, what you're looking for..."
                  rows={4}
                  maxLength={500}
                />
                <span className="connect-char-count">{profileData.bio.length}/500</span>
              </div>
            </div>
          )}

          {/* Step 3: Travel Preferences */}
          {onboardingStep === 3 && (
            <div className="connect-onboarding-step">
              <h3>Travel Preferences</h3>
              <p className="connect-step-desc">
                Help us connect you with like-minded travelers
              </p>

              <div className="connect-onboard-field">
                <label>Travel Style</label>
                <div className="connect-travel-styles">
                  {travelStyleOptions.map(style => (
                    <button
                      key={style}
                      type="button"
                      className={`connect-travel-style-btn ${profileData.travelStyle === style ? 'selected' : ''}`}
                      onClick={() => handleProfileChange('travelStyle', style)}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="connect-onboard-field">
                <label>Favorite Destinations</label>
                <div className="connect-destinations-input">
                  <input
                    type="text"
                    value={destinationInput}
                    onChange={(e) => setDestinationInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addDestination(destinationInput)}
                    placeholder="Type a destination and press Enter"
                    className="connect-onboard-input"
                  />
                </div>
                <div className="connect-suggested-destinations">
                  {suggestedDestinations
                    .filter(d => !profileData.favoriteDestinations.includes(d))
                    .slice(0, 5)
                    .map(dest => (
                      <button
                        key={dest}
                        type="button"
                        className="connect-suggested-btn"
                        onClick={() => addDestination(dest)}
                      >
                        + {dest}
                      </button>
                    ))}
                </div>
                {profileData.favoriteDestinations.length > 0 && (
                  <div className="connect-selected-destinations">
                    {profileData.favoriteDestinations.map(dest => (
                      <span key={dest} className="connect-destination-tag">
                        {dest}
                        <button onClick={() => removeDestination(dest)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="connect-onboarding-actions">
          <button
            type="button"
            className="connect-onboard-skip"
            onClick={handleOnboardingSkip}
          >
            Skip for now
          </button>
          <div className="connect-onboard-nav">
            {onboardingStep > 1 && (
              <button
                type="button"
                className="connect-onboard-back"
                onClick={() => setOnboardingStep(onboardingStep - 1)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="connect-onboard-next"
              onClick={handleOnboardingNext}
            >
              {onboardingStep === 3 ? 'Complete' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="connect-profile-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="profile" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="profile" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <Link to="/community-guidelines" className="connect-guidelines-link">
                Community Guidelines
              </Link>
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      {/* Profile Content */}
      <main className="cpx-main">
        {/* Cover */}
        <div className="cpx-cover">
          {(uploadPreview.cover || profileData.coverPhoto) ? (
            <img src={uploadPreview.cover || profileData.coverPhoto} alt="Cover" />
          ) : (
            <div className="cpx-cover-placeholder"></div>
          )}
          <button
            type="button"
            className="cpx-cover-edit"
            onClick={() => coverInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Edit cover'}
          </button>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleImageUpload(e.target.files[0], 'cover')}
          />
        </div>

        <div className="cpx-layout">
          {/* Identity card */}
          <aside className="cpx-card">
            <div
              className="cpx-avatar uploadable"
              onClick={() => avatarInputRef.current?.click()}
              title="Change profile picture"
            >
              {(uploadPreview.avatar || profileData.profilePicture) ? (
                <img src={uploadPreview.avatar || profileData.profilePicture} alt="Profile" />
              ) : (
                <div className="cpx-avatar-placeholder">
                  {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : '?'}
                </div>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleImageUpload(e.target.files[0], 'avatar')}
              />
            </div>

            <h1 className="cpx-name">{profileData.fullName || 'Your Name'}</h1>
            {profileData.pronouns && (
              <span className="cpx-pronouns">{profileData.pronouns}</span>
            )}

            {/* Right under the name, so editing is the first thing you see */}
            <div className="cpx-actions cpx-actions-top">
              <button className="cpx-btn primary" onClick={() => setIsEditing(true)}>
                Edit profile
              </button>
              <button className="cpx-btn ghost" onClick={() => setShowSetup(true)}>
                Travel &amp; interests
              </button>
            </div>

            <p className="cpx-based">
              <span className="cpx-based-label">Based in</span>
              <button
                type="button"
                className="cpx-based-value"
                onClick={() => setActiveTab('trips')}
              >
                {homeDestinationName || 'Add where you are based'}
              </button>
            </p>

            {profileData.currentLocation && (
              <p className="cpx-location">📍 {profileData.currentLocation}</p>
            )}

            <div className="cpx-stats">
              <button type="button" className="cpx-stat-btn" onClick={() => setFollowsTab('followers')}>
                <strong>{user.followers ?? 0}</strong>
                <span>Followers</span>
              </button>
              <button type="button" className="cpx-stat-btn" onClick={() => setFollowsTab('following')}>
                <strong>{user.following ?? 0}</strong>
                <span>Following</span>
              </button>
              <div>
                <strong>{posts.length}</strong>
                <span>Posts</span>
              </div>
            </div>

            {(profileData.genderIdentity || profileData.sexualOrientation || profileData.travelStyle) && (
              <>
                <div className="cpx-label">Identity</div>
                <div className="cpx-tags">
                  {profileData.genderIdentity && (
                    <span className="cpx-tag">{profileData.genderIdentity}</span>
                  )}
                  {profileData.sexualOrientation && (
                    <span className="cpx-tag">{profileData.sexualOrientation}</span>
                  )}
                  {profileData.travelStyle && (
                    <span className="cpx-tag travel">{profileData.travelStyle}</span>
                  )}
                </div>
              </>
            )}

            {profileData.bio && (
              <>
                <div className="cpx-label">About</div>
                <p className="cpx-bio">{profileData.bio}</p>
              </>
            )}

            {profileData.favoriteDestinations.length > 0 && (
              <>
                <div className="cpx-label">Favorite destinations</div>
                <div className="cpx-stamps">
                  {profileData.favoriteDestinations.map(dest => (
                    <span key={dest} className="cpx-stamp">{dest}</span>
                  ))}
                </div>
              </>
            )}

            {(!user.destinationId || !(user.tagIds && user.tagIds.length)) && (
              <div className="setup-banner">
                <h3>Finish setting up</h3>
                <p>
                  {!user.destinationId && !(user.tagIds && user.tagIds.length)
                    ? 'Tell us where you are and what you are into, so people can find you and we can say why you match.'
                    : !user.destinationId
                      ? 'Tell us where you are, so you show up in that destination.'
                      : 'Pick a few interests — with none, nobody can match with you.'}
                </p>
                <button className="cpx-btn primary" onClick={() => setShowSetup(true)}>
                  Add your details
                </button>
              </div>
            )}

            {!user.onboardingComplete && (
              <div className="cpx-incomplete">
                🌈 Complete your profile to connect with more travelers.
                <br />
                <button onClick={() => setShowOnboarding(true)}>Complete now</button>
              </div>
            )}
          </aside>

          {/* Feed */}
          <section className="cpx-feed">
            <div className="cpx-tabs">
              <button
                className={`cpx-tab ${activeTab === 'posts' ? 'active' : ''}`}
                onClick={() => setActiveTab('posts')}
              >
                Posts ({posts.length})
              </button>
              <button
                className={`cpx-tab ${activeTab === 'photos' ? 'active' : ''}`}
                onClick={() => setActiveTab('photos')}
              >
                Photos
              </button>
              <button
                className={`cpx-tab ${activeTab === 'trips' ? 'active' : ''}`}
                onClick={() => setActiveTab('trips')}
              >
                Location &amp; trips
              </button>
            </div>

            {activeTab === 'posts' && (
              <div className="connect-posts-list">
                {/* Post composer */}
                <div className="connect-post-composer">
                  <textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="Share your travel experience..."
                    rows={3}
                    maxLength={2000}
                  />
                  <ComposerImage
                    file={newPostImageFile}
                    onClear={() => {
                      setNewPostImageFile(null);
                      if (postImageInputRef.current) postImageInputRef.current.value = '';
                    }}
                  />

                  <div className="connect-composer-row">
                    <input
                      type="text"
                      value={newPostLocation}
                      onChange={(e) => setNewPostLocation(e.target.value)}
                      placeholder="📍 Location (optional)"
                      className="connect-composer-location"
                    />
                    <button
                      type="button"
                      className="connect-composer-photo-btn"
                      onClick={() => postImageInputRef.current?.click()}
                    >
                      {newPostImageFile ? '🖼️ Change photo' : '🖼️ Photo'}
                    </button>
                    <input
                      ref={postImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => setNewPostImageFile(e.target.files[0] || null)}
                    />
                    <button
                      type="button"
                      className="connect-composer-post-btn"
                      onClick={handleCreatePost}
                      disabled={isPosting || !newPostContent.trim()}
                    >
                      {isPosting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>

                {posts.length === 0 && (
                  <div className="cpx-state">
                    <h3>No posts yet</h3>
                    <p>Share your first travel experience above</p>
                  </div>
                )}

                {posts.map(post => (
                  <div key={post.id} className="connect-post-card">
                    <div className="connect-post-header">
                      <div className="connect-post-avatar">
                        {profileData.profilePicture ? (
                          <img src={profileData.profilePicture} alt="Profile" />
                        ) : (
                          <div className="connect-post-avatar-placeholder">
                            {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : '?'}
                          </div>
                        )}
                      </div>
                      <div className="connect-post-meta">
                        <span className="connect-post-author">{profileData.fullName || 'You'}</span>
                        <span className="connect-post-time">
                          {formatTimeAgo(post.timestamp)}
                          {post.location && ` · 📍 ${post.location}`}
                        </span>
                      </div>
                    </div>
                    <p className="connect-post-content">{post.content}</p>
                    {post.image && (
                      <div className="connect-post-image">
                        <img src={post.image} alt="Post" />
                      </div>
                    )}
                    <div className="connect-post-actions">
                      <button
                        className={`connect-post-action ${post.likedByViewer ? 'liked' : ''}`}
                        onClick={() => handleToggleLike(post.id)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={post.likedByViewer ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        <span>{post.likes}</span>
                      </button>
                      <button
                        className={`connect-post-action ${openCommentsPostId === post.id ? 'liked' : ''}`}
                        onClick={() => setOpenCommentsPostId(prev => prev === post.id ? null : post.id)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        <span>{post.comments}</span>
                      </button>
                      <button
                        className="connect-post-action connect-post-delete"
                        onClick={() => handleDeletePost(post.id)}
                        title="Delete post"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                    {openCommentsPostId === post.id && (
                      <PostComments
                        postId={post.id}
                        onCountChange={(postId, count) =>
                          setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: count } : p))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="connect-photos-grid">
                {posts.filter(p => p.image).length === 0 && (
                  <div className="cpx-state">
                    <h3>No photos yet</h3>
                    <p>Posts with photos show up here</p>
                  </div>
                )}
                {posts.filter(p => p.image).map(post => (
                  <div key={post.id} className="connect-photo-item">
                    <img src={post.image} alt="From a post" />
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'trips' && (
              <TravelPlans
                onHomeChange={(id) => setProfileData(prev => ({ ...prev, homeDestinationId: id }))}
              />
            )}
          </section>
        </div>
      </main>

      {followsTab && user && (
        <FollowsList
          userId={user.id}
          initialTab={followsTab}
          onClose={() => setFollowsTab(null)}
          onCountsChange={async () => {
            try {
              const res = await connectAuthPost('connect/profile/get');
              if (res.success) {
                const fresh = normalizeUser(res.data.user);
                setUser((prev) => ({ ...prev, followers: fresh.followers, following: fresh.following }));
              }
            } catch (err) { /* counts catch up on the next load */ }
          }}
        />
      )}

      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="connect-edit-overlay">
          <div className="connect-edit-modal">
            <div className="connect-edit-header">
              <h2>Edit Profile</h2>
              <button className="connect-edit-close" onClick={() => setIsEditing(false)}>×</button>
            </div>
            <div className="connect-edit-content">
              <div className="connect-edit-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                />
              </div>
              <div className="connect-edit-field">
                <label>Current Location</label>
                <input
                  type="text"
                  value={profileData.currentLocation}
                  onChange={(e) => handleProfileChange('currentLocation', e.target.value)}
                />
              </div>
              <div className="connect-edit-field">
                <label>Bio</label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  rows={4}
                />
              </div>
              {/* Until now this could only be set during onboarding, so a member
                  who wanted to go private later had no way to do it. */}
              <div className="connect-edit-field">
                <label>Who can see you</label>
                <div className="connect-visibility-options">
                  {visibilityOptions.map(opt => (
                    <button
                      type="button"
                      key={opt.value}
                      className={`connect-visibility-option ${profileData.visibility === opt.value ? 'selected' : ''}`}
                      onClick={() => handleProfileChange('visibility', opt.value)}
                      aria-pressed={profileData.visibility === opt.value}
                    >
                      <div className="connect-visibility-radio">
                        {profileData.visibility === opt.value && <div className="connect-radio-dot"></div>}
                      </div>
                      <div className="connect-visibility-info">
                        <span className="connect-visibility-label">{opt.label}</span>
                        <span className="connect-visibility-desc">{opt.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="connect-edit-actions">
              <button className="connect-edit-cancel" onClick={() => setIsEditing(false)}>Cancel</button>
              <button className="connect-edit-save" onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {showSetup && (
        <ConnectSetup
          user={user}
          onClose={() => setShowSetup(false)}
          onSaved={(updated) => { applyUser(updated); setShowSetup(false); }}
        />
      )}

      {/* Onboarding Modal */}
      {showOnboarding && renderOnboarding()}
    </div>
  );
}
